import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { isActiveEmp } from "../lib/constants";
import { EquipmentValidity } from "./EquipmentValidity";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Package, Bike, HardHat, Shield, Utensils, Shirt, Plus, Settings2, ArrowRightLeft,
  Undo2, Trash2, Pencil, Clock, User, ChevronDown, X, Loader2, PenLine, CheckCircle2, Printer, Boxes,
} from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { SignaturePad } from "./SignaturePad";
import { RentersPanel } from "./RentersPanel";
import { AssignAssetDialog } from "./AssignAssetDialog";
import { printReceipt, printGroupReceipt } from "../lib/custodyReceipt";
import { useAuth } from "../context/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);

const typeIcon = (name) => {
  if (name?.includes("دراجة") || name?.includes("دباب")) return Bike;
  if (name?.includes("خوذة") || name?.includes("خوذه")) return HardHat;
  if (name?.includes("سلامة") || name?.includes("سلامه")) return Shield;
  if (name?.includes("طعام") || name?.includes("صندوق")) return Utensils;
  if (name?.includes("زي") || name?.includes("لبس")) return Shirt;
  return Package;
};

// ---------- Asset create / edit dialog ----------
const AssetFormDialog = ({ open, onOpenChange, onSaved, types, branchOptions, employees, initial }) => {
  const { t, dir } = useLang();
  const editing = Boolean(initial);
  const [form, setForm] = useState(null);
  const [assign, setAssign] = useState({ employee_id: "", received_at: today(), note: "" });
  const [saving, setSaving] = useState(false);
  const [extraTypes, setExtraTypes] = useState([]);
  const sigRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm(initial
        ? { type: initial.type, identifier: initial.identifier || "", branch: initial.branch || "", notes: initial.notes || "" }
        : { type: types[0]?.name || "", identifier: "", branch: "", notes: "" });
      setAssign({ employee_id: "", received_at: today(), note: "" });
    }
  }, [open, initial, types]);

  if (!form) return null;

  const save = async () => {
    if (!form.type) { toast.warning(t("dash.custody.toast.chooseType")); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.updateAsset(initial.id, form);
        toast.success(t("dash.custody.toast.assetSaved"));
      } else {
        const payload = { ...form };
        if (assign.employee_id) {
          payload.employee_id = assign.employee_id;
          payload.received_at = assign.received_at;
          payload.note = assign.note;
          payload.signature_data = sigRef.current?.getSignature() || null;
        }
        await api.createAsset(payload);
        toast.success(t("dash.custody.toast.assetAdded"));
      }
      onOpenChange(false);
      onSaved();
    } catch { toast.error(t("dash.custody.toast.saveFail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">{editing ? t("dash.custody.form.editTitle") : t("dash.custody.form.addTitle")}</DialogTitle>
          <DialogDescription className="text-start">{t("dash.custody.form.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("dash.custody.form.type")}</Label>
              <Select value={form.type} onValueChange={async (v) => {
                if (v !== "__new") { setForm({ ...form, type: v }); return; }
                const n = (window.prompt("اسم نوع العهدة الجديد (مثال: جوال، لابتوب، مفتاح)") || "").trim();
                if (!n) return;
                try { await api.addCustodyType(n); setExtraTypes((x) => [...x, { id: `new-${n}`, name: n }]); setForm((f) => ({ ...f, type: n })); toast.success(`تمت إضافة «${n}»`); }
                catch { toast.error(t("dash.custody.toast.typeAddFail")); }
              }}>
                <SelectTrigger data-testid="asset-type-select"><SelectValue placeholder={t("dash.custody.form.chooseTypePh")} /></SelectTrigger>
                <SelectContent>
                  {[...types, ...extraTypes.filter((x) => !types.some((ty) => ty.name === x.name))].map((ty) => <SelectItem key={ty.id} value={ty.name}>{ty.name}</SelectItem>)}
                  {!editing && <SelectItem value="__new" alwaysShow className="font-semibold text-emerald-700" data-testid="asset-type-new"><span className="inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> نوع جديد</span></SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="asset-identifier">{t("dash.custody.form.identifier")}</Label>
              <Input id="asset-identifier" data-testid="asset-identifier-input" value={form.identifier}
                placeholder={t("dash.custody.form.identifierPh")} onChange={(e) => setForm({ ...form, identifier: e.target.value })} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="asset-branch">{t("dash.custody.form.branch")}</Label>
            <Input id="asset-branch" data-testid="asset-branch-input" list="custody-branches" value={form.branch}
              placeholder={t("dash.custody.form.branchPh")} onChange={(e) => setForm({ ...form, branch: e.target.value })} />
            <datalist id="custody-branches">
              {branchOptions.map((b) => <option key={b} value={b} />)}
            </datalist>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="asset-notes">{t("dash.custody.form.notes")}</Label>
            <Textarea id="asset-notes" data-testid="asset-notes-input" rows={2} value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>

          {!editing && (
            <div className="rounded-xl border border-blue-100 bg-blue-50/40 p-3 space-y-3">
              <p className="text-sm font-semibold text-blue-800 flex items-center gap-1.5">
                <User className="h-4 w-4" /> {t("dash.custody.form.assignOptional")}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>{t("dash.custody.form.employee")}</Label>
                  <Select value={assign.employee_id} onValueChange={(v) => setAssign({ ...assign, employee_id: v })}>
                    <SelectTrigger data-testid="asset-initial-employee-select"><SelectValue placeholder={t("dash.custody.form.chooseEmpPh")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__admin__">{t("dash.custody.form.admin")}</SelectItem>
                      {employees.filter(isActiveEmp).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.branch ? ` — ${e.branch}` : ""}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="asset-received">{t("dash.custody.form.receivedAt")}</Label>
                  <Input id="asset-received" type="date" data-testid="asset-received-input" value={assign.received_at}
                    onChange={(e) => setAssign({ ...assign, received_at: e.target.value })} />
                </div>
              </div>
              {assign.employee_id && (
                <>
                  <div className="space-y-1.5">
                    <Label>{t("dash.custody.form.handoverNote")}</Label>
                    <Input data-testid="asset-initial-note-input" value={assign.note}
                      onChange={(e) => setAssign({ ...assign, note: e.target.value })} placeholder={t("dash.custody.form.handoverNotePh")} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5" /> {t("dash.custody.form.receiverSig")}</Label>
                    <SignaturePad ref={sigRef} />
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="save-asset-button" disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 active:scale-95 transition gap-1.5" onClick={save}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("dash.custody.form.save")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.custody.form.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Group handover dialog ----------
const GroupAssignDialog = ({ open, onOpenChange, onSaved, employees, types }) => {
  const { t, dir } = useLang();
  const [form, setForm] = useState({ employee_id: "", received_at: today(), note: "" });
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const sigRef = useRef(null);

  useEffect(() => {
    if (open) {
      setForm({ employee_id: "", received_at: today(), note: "" });
      setItems([{ type: types[0]?.name || "", identifier: "" }]);
    }
  }, [open, types]);

  const addRow = () => setItems((p) => [...p, { type: types[0]?.name || "", identifier: "" }]);
  const removeRow = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const setRow = (i, key, val) => setItems((p) => p.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));

  const save = async () => {
    if (!form.employee_id) { toast.warning(t("dash.custody.toast.chooseEmp")); return; }
    const valid = items.filter((it) => it.type);
    if (valid.length === 0) { toast.warning(t("dash.custody.toast.addOneItem")); return; }
    setSaving(true);
    try {
      await api.createBatch({ ...form, items: valid, signature_data: sigRef.current?.getSignature() || null });
      toast.success(t("dash.custody.toast.batchDone", { n: valid.length }));
      onOpenChange(false);
      onSaved();
    } catch { toast.error(t("dash.custody.toast.batchFail")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg font-body max-h-[90vh] overflow-y-auto" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">{t("dash.custody.batch.title")}</DialogTitle>
          <DialogDescription className="text-start">{t("dash.custody.batch.desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>{t("dash.custody.batch.receiver")}</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm({ ...form, employee_id: v })}>
                <SelectTrigger data-testid="batch-employee-select"><SelectValue placeholder={t("dash.custody.form.chooseEmpPh")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__admin__">{t("dash.custody.form.admin")}</SelectItem>
                  {employees.filter(isActiveEmp).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}{e.branch ? ` — ${e.branch}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="batch-received">{t("dash.custody.form.receivedAt")}</Label>
              <Input id="batch-received" type="date" data-testid="batch-received-input" value={form.received_at}
                onChange={(e) => setForm({ ...form, received_at: e.target.value })} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("dash.custody.batch.items")}</Label>
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2" data-testid={`batch-item-row-${i}`}>
                <Select value={it.type} onValueChange={(v) => setRow(i, "type", v)}>
                  <SelectTrigger data-testid={`batch-item-type-${i}`} className="flex-1"><SelectValue placeholder={t("dash.custody.batch.typePh")} /></SelectTrigger>
                  <SelectContent>
                    {types.map((ty) => <SelectItem key={ty.id} value={ty.name}>{ty.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input data-testid={`batch-item-identifier-${i}`} className="flex-1" placeholder={t("dash.custody.batch.identifierPh")}
                  value={it.identifier} onChange={(e) => setRow(i, "identifier", e.target.value)} />
                <button type="button" data-testid={`batch-remove-item-${i}`} onClick={() => removeRow(i)}
                  disabled={items.length === 1}
                  className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition disabled:opacity-30">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" data-testid="batch-add-item-button" onClick={addRow} className="gap-1.5">
              <Plus className="h-4 w-4" /> {t("dash.custody.batch.addItem")}
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label>{t("dash.custody.batch.note")}</Label>
            <Input data-testid="batch-note-input" value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder={t("dash.custody.batch.notePh")} />
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5" /> {t("dash.custody.batch.receiverSigUnified")}</Label>
            <SignaturePad ref={sigRef} />
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="confirm-batch-button" disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 gap-1.5" onClick={save}>
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {t("dash.custody.batch.submit")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.custody.form.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Types management dialog ----------
const TypesDialog = ({ open, onOpenChange, types, onChanged }) => {
  const { t, dir } = useLang();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const add = async () => {
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try { await api.addCustodyType(n); setName(""); onChanged(); } catch { toast.error(t("dash.custody.toast.typeAddFail")); }
    finally { setBusy(false); }
  };
  const remove = async (id) => {
    try { await api.deleteCustodyType(id); onChanged(); } catch { toast.error(t("dash.custody.toast.typeDelFail")); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-body" dir={dir}>
        <DialogHeader className="text-start">
          <DialogTitle className="font-heading text-xl">{t("dash.custody.typesDlg.title")}</DialogTitle>
          <DialogDescription className="text-start">{t("dash.custody.typesDlg.desc")}</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input data-testid="new-type-input" value={name} onChange={(e) => setName(e.target.value)}
            placeholder={t("dash.custody.typesDlg.newNamePh")} onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button data-testid="add-type-button" disabled={busy} onClick={add} className="bg-blue-600 hover:bg-blue-700 shrink-0 gap-1">
            <Plus className="h-4 w-4" /> {t("dash.custody.typesDlg.add")}
          </Button>
        </div>
        <div className="space-y-2 max-h-60 overflow-y-auto pt-1">
          {types.map((ty) => (
            <div key={ty.id} data-testid={`type-row-${ty.name}`} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <span className="text-sm text-slate-700">{ty.name}</span>
              <button data-testid={`delete-type-${ty.name}`} onClick={() => remove(ty.id)} className="text-slate-400 hover:text-red-600 transition">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Timeline of one asset ----------
const Timeline = ({ asset, assignments, employees = [] }) => {
  const { t } = useLang();
  const { company } = useAuth();
  return (
    <div className="mt-3 border-t border-slate-100 pt-3 space-y-3" data-testid="custody-timeline">
      {[...assignments].reverse().map((a, i) => {
        const isCurrent = !a.returned_at;
        return (
          <div key={a.id} className="flex gap-3">
            <div className="flex flex-col items-center">
              <span className={`h-3 w-3 rounded-full ${isCurrent ? "bg-blue-500 ring-4 ring-blue-100" : "bg-slate-300"}`} />
              {i < assignments.length - 1 && <span className="w-px flex-1 bg-slate-200 my-1" />}
            </div>
            <div className="flex-1 pb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-slate-800 text-sm">{a.employee_name}</span>
                {isCurrent && <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] rounded-full">{t("dash.custody.timeline.current")}</Badge>}
                <button data-testid={`receipt-${a.id}`} onClick={() => printReceipt(asset, { ...a, iqama_number: (employees.find((e) => e.id === a.employee_id) || {}).iqama_number || "" }, { company })}
                  className="ms-auto inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-700 transition">
                  <Printer className="h-3.5 w-3.5" /> {t("dash.custody.timeline.receipt")}
                </button>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {t("dash.custody.timeline.receivedOn", { d: a.received_at })}{a.returned_at ? ` ${t("dash.custody.timeline.handedOn", { d: a.returned_at })}` : ` ${t("dash.custody.timeline.untilNow")}`}
              </p>
              {a.note && <p className="text-xs text-slate-600 mt-0.5">📝 {a.note}</p>}
              {a.signature_id && (
                <a href={fileUrl(a.signature_id)} target="_blank" rel="noreferrer"
                  data-testid={`signature-link-${a.id}`}
                  className="inline-block mt-1 rounded border border-slate-200 bg-white p-1 hover:border-blue-300 transition">
                  <img src={fileUrl(a.signature_id)} alt={t("dash.custody.timeline.sigAlt")} className="h-9 object-contain" />
                </a>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ---------- Main panel ----------
export const CustodyPanel = ({ employees = [], branchOptions = [], canEdit, isManager }) => {
  const { t, dir } = useLang();
  const { company } = useAuth();
  const [assets, setAssets] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branch, setBranch] = useState("all");
  const [tab, setTab] = useState("assets");
  const [expanded, setExpanded] = useState({});
  const [openCards, setOpenCards] = useState({});
  const [assetDialog, setAssetDialog] = useState(false);
  const [groupDialog, setGroupDialog] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assignTarget, setAssignTarget] = useState(null);
  const [returnTarget, setReturnTarget] = useState(null);
  const [returnAllTarget, setReturnAllTarget] = useState(null);
  const [typesDialog, setTypesDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, ty] = await Promise.all([api.custodyAssets(branch), api.custodyTypes()]);
      setAssets(a); setTypes(ty);
    } catch { toast.error(t("dash.custody.toast.loadFail")); }
    finally { setLoading(false); }
  }, [branch, t]);

  useEffect(() => { load(); }, [load]);

  const doReturn = async () => {
    try {
      await api.returnAsset(returnTarget.id, { returned_at: today() });
      toast.success(t("dash.custody.toast.returned"));
      setReturnTarget(null);
      load();
    } catch { toast.error(t("dash.custody.toast.returnFail")); }
  };

  const doReturnAll = async () => {
    try {
      const res = await api.returnAll(returnAllTarget.empId);
      toast.success(t("dash.custody.toast.returnedN", { n: res.returned }));
      setReturnAllTarget(null);
      load();
    } catch { toast.error(t("dash.custody.toast.returnFail")); }
  };

  const doDelete = async () => {
    try {
      await api.deleteAsset(deleteTarget.id);
      toast.success(t("dash.custody.toast.deleted"));
      setDeleteTarget(null);
      load();
    } catch { toast.error(t("dash.custody.toast.deleteFail")); }
  };

  const [q, setQ] = useState("");
  const [fAssign, setFAssign] = useState("all");
  const [fType, setFType] = useState("");
  const typeOpts = useMemo(() => [...new Set(assets.map((a) => a.type).filter(Boolean))].sort(), [assets]);
  const kpi = useMemo(() => ({ total: assets.length, assigned: assets.filter((a) => a.current).length,
    holders: new Set(assets.filter((a) => a.current).map((a) => a.current.employee_id)).size }), [assets]);
  const shownAssets = useMemo(() => {
    const k = q.trim().toLowerCase();
    return assets.filter((a) => (fAssign === "all" || (fAssign === "assigned" ? !!a.current : !a.current)) && (!fType || a.type === fType)
      && (!k || `${a.type || ""} ${a.identifier || ""} ${a.current?.employee_name || ""} ${a.current?.branch || ""}`.toLowerCase().includes(k)));
  }, [assets, q, fAssign, fType]);
  const byEmployee = useMemo(() => {
    const map = {};
    shownAssets.forEach((a) => {
      if (!a.current) return;
      const key = a.current.employee_id;
      if (!map[key]) map[key] = { empId: key, name: a.current.employee_name, branch: a.current.branch, iqama: (employees.find((e) => e.id === key) || {}).iqama_number || "", items: [] };
      map[key].items.push({ id: a.id, type: a.type, identifier: a.identifier, since: a.current.received_at, signature_id: a.current.signature_id, note: a.current.note });
    });
    return Object.values(map).sort((x, y) => y.items.length - x.items.length);
  }, [shownAssets]); // eslint-disable-line react-hooks/exhaustive-deps

  const assetGroups = useMemo(() => {
    const map = {};
    shownAssets.forEach((a) => {
      const key = a.current ? a.current.employee_id : "__none__";
      if (!map[key]) map[key] = {
        key, isUnassigned: !a.current,
        name: a.current ? a.current.employee_name : t("dash.custody.unassignedName"),
        branch: a.current ? a.current.branch : "", assets: [],
      };
      map[key].assets.push(a);
    });
    return Object.values(map).sort((x, y) => (x.isUnassigned ? 1 : 0) - (y.isUnassigned ? 1 : 0) || y.assets.length - x.assets.length);
  }, [shownAssets, t]);

  const empFor = (g) => ({
    empId: g.key, name: g.name, branch: g.branch, iqama: (employees.find((e) => e.id === g.key) || {}).iqama_number || "",
    items: g.assets.map((a) => ({ id: a.id, type: a.type, identifier: a.identifier, since: a.current?.received_at, signature_id: a.current?.signature_id, note: a.current?.note })),
  });

  const toggleCard = (k) => setOpenCards((p) => ({ ...p, [k]: !p[k] }));
  const currentKeys = tab === "assets" ? assetGroups.map((g) => g.key) : byEmployee.map((e) => e.empId);
  const allOpen = currentKeys.length > 0 && currentKeys.every((k) => openCards[k]);
  const toggleAll = () => {
    if (allOpen) { setOpenCards({}); return; }
    const n = {};
    currentKeys.forEach((k) => { n[k] = true; });
    setOpenCards(n);
  };

  const iconBtn = "p-1.5 rounded-lg text-slate-400 transition";

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
      className="space-y-5" data-testid="custody-panel">
      {tab !== "renters" && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="custody-kpis">
          {[["all", "إجمالي العهد", kpi.total, "text-slate-800", () => { setFAssign("all"); setFType(""); setQ(""); }],
            ["assigned", "مسلَّمة لموظفين", kpi.assigned, "text-blue-600", () => setFAssign("assigned")],
            ["none", "غير مسلَّمة", kpi.total - kpi.assigned, "text-amber-600", () => setFAssign("none")],
            ["holders", "موظفون لديهم عهد", kpi.holders, "text-emerald-600", () => { setFAssign("assigned"); setTab("byEmployee"); }]].map(([k, l, v, tone, fn]) => (
            <button key={k} type="button" onClick={fn} data-testid={`custody-kpi-${k}`}
              className={`rounded-2xl border bg-white p-4 text-start transition hover:shadow-md ${fAssign === k ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-200"}`}>
              <p className="text-xs text-slate-500">{l}</p>
              <p className={`font-heading text-xl font-bold tabular-nums ${tone}`}>{v}</p>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Tabs value={tab} onValueChange={setTab} dir={dir}>
            <TabsList data-testid="custody-view-tabs" className="max-w-[calc(100vw-2rem)] justify-start overflow-x-auto">
              <TabsTrigger value="assets" data-testid="custody-tab-assets">{t("dash.custody.tabs.assets")}</TabsTrigger>
              <TabsTrigger value="byEmployee" data-testid="custody-tab-employees">{t("dash.custody.tabs.byEmployee")}</TabsTrigger>
              <TabsTrigger value="renters" data-testid="custody-tab-renters">حسابات المستأجرين</TabsTrigger>
              <TabsTrigger value="validity" data-testid="custody-tab-validity">صلاحية المعدات</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {tab !== "renters" && (
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="sm" data-testid="toggle-all-cards-button" className="gap-1.5" onClick={toggleAll}>
            <ChevronDown className={`h-4 w-4 transition-transform ${allOpen ? "rotate-180" : ""}`} />
            <span className="hidden sm:inline">{allOpen ? t("dash.custody.collapseAll") : t("dash.custody.expandAll")}</span>
          </Button>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث: موظف، نوع، رقم العهدة" data-testid="custody-search"
            className="h-9 w-52 rounded-md border border-slate-200 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
          <select value={fType} onChange={(e) => setFType(e.target.value)} data-testid="custody-type-filter" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
            <option value="">كل الأنواع</option>{typeOpts.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select value={fAssign} onChange={(e) => setFAssign(e.target.value)} data-testid="custody-assign-filter" className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
            <option value="all">الكل</option><option value="assigned">مسلَّمة</option><option value="none">غير مسلَّمة</option>
          </select>
          <Select value={branch} onValueChange={setBranch}>
            <SelectTrigger data-testid="custody-branch-select" className="w-36"><SelectValue placeholder={t("dash.custody.allBranches")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("dash.custody.allBranches")}</SelectItem>
              {branchOptions.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
            </SelectContent>
          </Select>
          {isManager && (
            <Button variant="outline" size="sm" data-testid="manage-types-button" className="gap-1.5" onClick={() => setTypesDialog(true)}>
              <Settings2 className="h-4 w-4" /> <span className="hidden sm:inline">{t("dash.custody.types")}</span>
            </Button>
          )}
          {canEdit && (
            <Button variant="outline" size="sm" data-testid="group-assign-button" className="gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
              onClick={() => setGroupDialog(true)}>
              <Boxes className="h-4 w-4" /> {t("dash.custody.groupAssign")}
            </Button>
          )}
          {canEdit && (
            <Button size="sm" data-testid="add-asset-button" className="bg-blue-600 hover:bg-blue-700 gap-1.5"
              onClick={() => { setEditingAsset(null); setAssetDialog(true); }}>
              <Plus className="h-4 w-4" /> {t("dash.custody.addAsset")}
            </Button>
          )}
        </div>
        )}
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400">{t("dash.custody.loading")}</div>
      ) : tab === "validity" ? (
        <EquipmentValidity employees={employees} canEdit={canEdit} />
      ) : tab === "renters" ? (
        <RentersPanel branchOptions={branchOptions} canEdit={canEdit} />
      ) : tab === "assets" ? (
        assets.length === 0 ? (
          <div className="py-16 text-center text-slate-400" data-testid="custody-empty">{t("dash.custody.emptyAssets")}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {assetGroups.map((g) => {
              const cardOpen = openCards[g.key];
              return (
              <div key={g.key} data-testid={`custody-group-${g.key}`} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div data-testid={`custody-group-header-${g.key}`} onClick={() => toggleCard(g.key)}
                  className={`flex items-center gap-2 cursor-pointer select-none ${cardOpen ? "mb-3" : ""}`}>
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center font-bold shrink-0 ${g.isUnassigned ? "bg-slate-100 text-slate-400" : "bg-blue-100 text-blue-700"}`}>
                    {g.isUnassigned ? <CheckCircle2 className="h-5 w-5" /> : (g.name || "؟").charAt(0)}
                  </div>
                  <div>
                    <p className="font-heading font-bold text-slate-800">{g.name}</p>
                    {g.branch && <p className="text-xs text-slate-400">{g.branch}</p>}
                  </div>
                  <Badge className="ms-auto bg-blue-50 text-blue-700 border-blue-200 rounded-full">{t("dash.custody.countBadge", { n: g.assets.length })}</Badge>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${cardOpen ? "rotate-180" : ""}`} />
                </div>

                {cardOpen && (<>
                {!g.isUnassigned && (
                  <div className="flex items-center gap-3 mb-2">
                    <button data-testid={`group-receipt-${g.key}`} onClick={() => printGroupReceipt(empFor(g), company)}
                      className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-700 transition">
                      <Printer className="h-3.5 w-3.5" /> {t("dash.custody.groupReceipt")}
                    </button>
                    {canEdit && (
                      <button data-testid={`group-return-${g.key}`} onClick={() => setReturnAllTarget(empFor(g))}
                        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-600 transition">
                        <Undo2 className="h-3.5 w-3.5" /> {t("dash.custody.returnAll")}
                      </button>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  {g.assets.map((a) => {
                    const Icon = typeIcon(a.type);
                    const open = expanded[a.id];
                    return (
                      <div key={a.id} data-testid={`asset-card-${a.id}`} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Icon className="h-4 w-4 text-blue-600 shrink-0" />
                          <span className="font-semibold text-slate-800 text-sm">{a.type}</span>
                          {a.identifier && <span className="text-xs text-slate-500">· {a.identifier}</span>}
                          {a.branch && <Badge variant="outline" className="rounded-full text-[10px] border-slate-200 text-slate-500">{a.branch}</Badge>}
                          <div className="ms-auto flex items-center gap-0.5">
                            {canEdit && (
                              <button title={t("dash.custody.titles.assign")} data-testid={`assign-asset-${a.id}`} onClick={() => setAssignTarget(a)}
                                className={`${iconBtn} hover:text-blue-700 hover:bg-blue-50`}><ArrowRightLeft className="h-4 w-4" /></button>
                            )}
                            {canEdit && a.current && (
                              <button title={t("dash.custody.titles.ret")} data-testid={`return-asset-${a.id}`} onClick={() => setReturnTarget(a)}
                                className={`${iconBtn} hover:text-amber-700 hover:bg-amber-50`}><Undo2 className="h-4 w-4" /></button>
                            )}
                            {a.current && (
                              <button title={t("dash.custody.titles.receipt")} data-testid={`receipt-current-${a.id}`} onClick={() => printReceipt(a, { ...a.current, iqama_number: (employees.find((e) => e.id === a.current.employee_id) || {}).iqama_number || "" }, { company })}
                                className={`${iconBtn} hover:text-slate-700 hover:bg-slate-100`}><Printer className="h-4 w-4" /></button>
                            )}
                            {canEdit && (
                              <button title={t("dash.custody.titles.edit")} data-testid={`edit-asset-${a.id}`} onClick={() => { setEditingAsset(a); setAssetDialog(true); }}
                                className={`${iconBtn} hover:text-slate-700 hover:bg-slate-100`}><Pencil className="h-4 w-4" /></button>
                            )}
                            {canEdit && (
                              <button title={t("dash.custody.titles.del")} data-testid={`delete-asset-${a.id}`} onClick={() => setDeleteTarget(a)}
                                className={`${iconBtn} hover:text-red-600 hover:bg-red-50`}><Trash2 className="h-4 w-4" /></button>
                            )}
                          </div>
                        </div>
                        {a.current
                          ? <p className="text-xs text-slate-400 mt-1">{t("dash.custody.since", { d: a.current.received_at })}</p>
                          : <p className="text-xs text-slate-400 mt-1">{t("dash.custody.notAssignedNow")}</p>}
                        {a.notes && <p className="text-xs text-slate-500 mt-0.5">{a.notes}</p>}
                        {a.assignment_count > 0 && (
                          <button data-testid={`toggle-timeline-${a.id}`} onClick={() => setExpanded({ ...expanded, [a.id]: !open })}
                            className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-700 transition">
                            <Clock className="h-3.5 w-3.5" /> {t("dash.custody.chainToggle", { n: a.assignment_count })}
                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
                          </button>
                        )}
                        {open && a.assignment_count > 0 && <Timeline asset={a} assignments={a.assignments} employees={employees} />}
                      </div>
                    );
                  })}
                </div>
                </>)}
              </div>
              );
            })}
          </div>
        )
      ) : (
        byEmployee.length === 0 ? (
          <div className="py-16 text-center text-slate-400" data-testid="custody-byemp-empty">{t("dash.custody.emptyByEmp")}</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {byEmployee.map((emp) => {
              const cardOpen = openCards[emp.empId];
              return (
              <div key={emp.empId} data-testid="custody-employee-card" className="rounded-2xl border border-slate-200 bg-white p-4">
                <div data-testid={`custody-employee-header-${emp.empId}`} onClick={() => toggleCard(emp.empId)}
                  className={`flex items-center gap-2 cursor-pointer select-none ${cardOpen ? "mb-3" : ""}`}>
                  <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold">{(emp.name || "؟").charAt(0)}</div>
                  <div>
                    <p className="font-heading font-bold text-slate-800">{emp.name}</p>
                    {emp.branch && <p className="text-xs text-slate-400">{emp.branch}</p>}
                  </div>
                  <Badge className="ms-auto bg-blue-50 text-blue-700 border-blue-200 rounded-full">{t("dash.custody.countBadge", { n: emp.items.length })}</Badge>
                  <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${cardOpen ? "rotate-180" : ""}`} />
                </div>
                {cardOpen && (<>
                <div className="flex items-center gap-3 mb-2">
                  <button data-testid="group-receipt-button" onClick={() => printGroupReceipt(emp, company)}
                    className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-700 transition">
                    <Printer className="h-3.5 w-3.5" /> {t("dash.custody.groupReceipt")}
                  </button>
                  {canEdit && (
                    <button data-testid="return-all-button" onClick={() => setReturnAllTarget(emp)}
                      className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-red-600 transition">
                      <Undo2 className="h-3.5 w-3.5" /> {t("dash.custody.returnAll")}
                    </button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {emp.items.map((it, j) => {
                    const Icon = typeIcon(it.type);
                    return (
                      <div key={j} className="flex items-center gap-2 text-sm rounded-lg bg-slate-50 px-3 py-2">
                        <Icon className="h-4 w-4 text-blue-600 shrink-0" />
                        <span className="text-slate-700">{it.type}{it.identifier ? ` · ${it.identifier}` : ""}</span>
                        <span className="ms-auto text-xs text-slate-400">{t("dash.custody.since", { d: it.since })}</span>
                      </div>
                    );
                  })}
                </div>
                </>)}
              </div>
              );
            })}
          </div>
        )
      )}

      <AssetFormDialog open={assetDialog} onOpenChange={(v) => { setAssetDialog(v); if (!v) setEditingAsset(null); }}
        onSaved={load} types={types} branchOptions={branchOptions} employees={employees} initial={editingAsset} />
      <AssignAssetDialog asset={assignTarget} onOpenChange={() => setAssignTarget(null)} onSaved={load} employees={employees} />
      <GroupAssignDialog open={groupDialog} onOpenChange={setGroupDialog} onSaved={load} employees={employees} types={types} />
      <TypesDialog open={typesDialog} onOpenChange={setTypesDialog} types={types} onChanged={load} />

      <AlertDialog open={Boolean(returnTarget)} onOpenChange={(v) => !v && setReturnTarget(null)}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-start">{t("dash.custody.confirm.returnTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-start">
              {t("dash.custody.confirm.returnDesc", { item: `${returnTarget?.type || ""}${returnTarget?.identifier ? ` · ${returnTarget.identifier}` : ""}`, name: returnTarget?.current?.employee_name || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction data-testid="confirm-return-button" onClick={doReturn} className="bg-blue-600 hover:bg-blue-700">{t("dash.custody.confirm.doReturn")}</AlertDialogAction>
            <AlertDialogCancel>{t("dash.custody.confirm.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(returnAllTarget)} onOpenChange={(v) => !v && setReturnAllTarget(null)}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-start">{t("dash.custody.confirm.returnAllTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-start">
              {t("dash.custody.confirm.returnAllDesc", { n: returnAllTarget?.items?.length || 0, name: returnAllTarget?.name || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction data-testid="confirm-return-all-button" onClick={doReturnAll} className="bg-blue-600 hover:bg-blue-700">{t("dash.custody.confirm.doReturnAll")}</AlertDialogAction>
            <AlertDialogCancel>{t("dash.custody.confirm.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent dir={dir} className="font-body">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-start">{t("dash.custody.confirm.deleteTitle")}</AlertDialogTitle>
            <AlertDialogDescription className="text-start">
              {t("dash.custody.confirm.deleteDesc", { item: `${deleteTarget?.type || ""}${deleteTarget?.identifier ? ` · ${deleteTarget.identifier}` : ""}` })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <AlertDialogAction data-testid="confirm-delete-asset-button" onClick={doDelete} className="bg-red-600 hover:bg-red-700">{t("dash.custody.confirm.doDelete")}</AlertDialogAction>
            <AlertDialogCancel>{t("dash.custody.confirm.cancel")}</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
};
